import { useCallback, useEffect, useMemo, useState } from 'react';
import useUrlParams from '../../hooks/useUrlParams';
import { Alert, App, Button, Dropdown, Empty, List, Switch, Tag, Typography } from 'antd';

import ExportDefinitionEditor from './ExportDefinitionEditor';
import ExportRunsModal from './ExportRunsModal';
import TabLoadState, { RefreshFailedNotice } from '../common/TabLoadState';
import UserRef from '../users/UserRef';
import { buildActionMenu } from '../common/actionMenu';
import {
	deleteExportDefinition,
	disableExportDefinition,
	enableExportDefinition,
	getExportDefinitions,
	runExportNow,
} from '../../services/exportDefinitions';
import { getSavedFilters } from '../../services/savedFilters';
import { getSharing } from '../../services/sharing';
import {
	DISABLED_REASON_WORDS,
	RUN_STATUS_COLORS,
	RUN_STATUS_LABELS,
	describeDefinition,
	describeRecipients,
	eligibleAccountRecipients,
	limitsOf,
} from '../../utils/exportDefinitions';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { retryAfterMs } from '../../utils/retryAfter';
import { formatCount } from '../../utils/counts';
import { getCollectionMemberCardIds } from '../../utils/collections';
import { normalizeId } from '../../utils/ids';
import { getUserId } from '../../utils';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useDateFormat from '../../hooks/useDateFormat';
import { HISTORY, KEBAB, PLUS, SEND } from '../icons';
import { confirmDestructive } from '../common/confirmDestructive';

const { Text } = Typography;

/**
 * Exports tab (managers only; CollectionForm gates the tab and the API's
 * export routes are manage-gated besides): the collection's STORED exports —
 * named definitions that render on a schedule, or when their owner runs
 * them, and email each recipient a download link (keepr-api docs/SCHEMA.md §
 * Stored exports).
 *
 * Like Sharing and Automations, everything here applies IMMEDIATELY through
 * its own endpoints, outside the settings page's Save button.
 *
 * Each row answers what a manager arrives asking: what it sends and when,
 * who it reaches, whose access it runs with, whether the last run worked —
 * and, when keepr switched it off, why, in words. Its verbs sit in one ⋯ in
 * the platform taxonomy; the enabled switch stays on the row because pausing
 * one is a runtime decision, not a re-authoring.
 *
 * ⚠ OWNERSHIP. A run renders through its owner's access; any edit or enable
 * makes the person doing it the owner, so switching someone else's export on
 * asks first. Run now is the owner's alone (the API answers 403
 * `not_export_owner` to anyone else), so for everyone else it is withheld
 * with its reason.
 */

const currentUserId = () => {
	const id = getUserId();
	return id && id !== 'undefined' ? id : null;
};

const VIEW_PARAMS = { runsExport: { trim: false }, runsPage: { default: 1, kind: 'page' } };

/** The open runs ledger and its page live in the URL, like the automations one (hooks/useUrlParams). */
function useExportsViewParams() {
	const { params: { runsExport, runsPage }, update } = useUrlParams( VIEW_PARAMS );
	return { runsExport, runsPage, update };
}

function LastRun( { definition } ) {
	const status = definition?.lastRunStatus;
	if ( ! status ) return <Text type='secondary'>Not run yet</Text>;
	return (
		<span className='exports-list__last-run'>
			<Tag color={RUN_STATUS_COLORS[ status ] || 'default'}>{RUN_STATUS_LABELS[ status ] || status}</Tag>
			<Text type='secondary'>{formatDisplayDateTime( definition.lastRunAt )}</Text>
		</span>
	);
}

/** Copy the parts a duplicate keeps: everything a person authored. */
const duplicateOf = definition => ( {
	name: `${ definition?.name || 'Export' } (copy)`.slice( 0, 100 ),
	subject: definition?.subject,
	format: definition?.format,
	options: definition?.options,
	schedule: definition?.schedule || null,
	retentionDays: definition?.retentionDays,
	delivery: definition?.delivery,
} );

export default function ExportsTab( { collectionId, collection, prefill = null, onPrefillUsed } ) {

	const { message, modal } = App.useApp();
	const { runsExport, runsPage, update } = useExportsViewParams();
	const me = currentUserId();

	const [ definitions, setDefinitions ] = useState( [] );
	const [ channels, setChannels ] = useState( {} );
	const [ limits, setLimits ] = useState( {} );
	const [ loading, setLoading ] = useState( true );
	const [ loadError, setLoadError ] = useState( null );
	// The first read has answered: until then the tab is a skeleton, after it the tab keeps its data.
	const [ loaded, setLoaded ] = useState( false );
	const [ savedFilters, setSavedFilters ] = useState( [] );
	const [ sharing, setSharing ] = useState( null );
	const [ sharingLoading, setSharingLoading ] = useState( false );
	// null = closed; { definition } to edit; { prefill } to create.
	const [ editing, setEditing ] = useState( null );
	const [ running, setRunning ] = useState( null );

	useDateFormat();

	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = useMemo( () => cardDefinitions?.cardsById || {}, [ cardDefinitions ] );
	const getElementsForCard = cardDefinitions?.getElementsForCard;
	const collectionCards = useMemo( () => {
		const memberIds = getCollectionMemberCardIds( collection, Object.values( cardsById ) );
		return Object.values( cardsById ).filter( card => memberIds.has( normalizeId( card?._id ) ) );
	}, [ collection, cardsById ] );

	const bounds = limitsOf( limits );

	const reload = useCallback( () => {
		if ( ! collectionId ) return Promise.resolve();
		return getExportDefinitions( collectionId )
			.then( data => {
				setDefinitions( data.exports );
				setChannels( data.channels );
				setLimits( data.limits );
				setLoadError( null );
				setLoaded( true );
			} )
			.catch( error => setLoadError( error ) );
	}, [ collectionId ] );

	useEffect( () => {
		setLoading( true );
		reload().finally( () => setLoading( false ) );
	}, [ reload ] );

	// The saved filters name a subject in the list and are the editor's first
	// choice. A list that will not load degrades to "a saved filter".
	useEffect( () => {
		if ( ! collectionId ) return undefined;
		let active = true;
		getSavedFilters( collectionId )
			.then( rows => { if ( active ) setSavedFilters( rows ); } )
			.catch( () => { if ( active ) setSavedFilters( [] ); } );
		return () => { active = false; };
	}, [ collectionId ] );

	// Who may receive an export by account: the collection's collection-wide
	// collaborators. Loaded when an editor first opens, not before.
	useEffect( () => {
		if ( ! editing || sharing || sharingLoading || ! collectionId ) return;
		setSharingLoading( true );
		getSharing( collectionId )
			.then( data => setSharing( data || {} ) )
			.catch( () => setSharing( {} ) )
			.finally( () => setSharingLoading( false ) );
	}, [ editing, sharing, sharingLoading, collectionId ] );
	const recipientOptions = useMemo( () => eligibleAccountRecipients( sharing ), [ sharing ] );

	// The bridge from the export dialog: "Save this export…" lands here with
	// the subject, format and options already chosen.
	useEffect( () => {
		if ( prefill ) setEditing( { prefill } );
	}, [ prefill ] );

	const closeEditor = () => {
		setEditing( null );
		onPrefillUsed?.();
	};

	const setEnabled = useCallback( ( definition, next ) => {
		const id = normalizeId( definition?._id );
		// Optimistic, rolled back if the API refuses — the house pattern for a
		// one-field toggle.
		const apply = value => setDefinitions( prev => prev.map(
			row => ( normalizeId( row?._id ) === id ? { ...row, enabled: value } : row )
		) );
		apply( next );
		( next ? enableExportDefinition : disableExportDefinition )( collectionId, id )
			.then( saved => {
				if ( saved?._id ) {
					setDefinitions( prev => prev.map( row => ( normalizeId( row?._id ) === id ? saved : row ) ) );
				}
			} )
			.catch( err => {
				apply( ! next );
				message.error( err?.response?.data?.message || 'Unable to change this export right now.' );
			} );
	}, [ collectionId, message ] );

	const handleToggle = useCallback( ( definition, next ) => {
		const ownerId = normalizeId( definition?.ownerId );
		// Switching it ON is deciding it sends: the API makes the person who
		// does it the owner, so a manager switching on someone else's export
		// is asked first. Switching off widens nothing and never re-owns.
		if ( next && ( ! ownerId || ownerId !== me ) ) {
			modal.confirm( {
				title: `Switch on "${ definition?.name || 'this export' }"?`,
				content: 'Switching it on makes you the owner; this export will run with your access.',
				okText: 'Switch on',
				onOk: () => setEnabled( definition, true ),
			} );
			return;
		}
		setEnabled( definition, next );
	}, [ me, modal, setEnabled ] );

	const runNow = useCallback( async definition => {
		const id = normalizeId( definition?._id );
		setRunning( id );
		try {
			const run = await runExportNow( collectionId, id );
			// A skip is not a success, and "sent" over one would be the lie
			// the run ledger exists to prevent.
			if ( run?.status === 'skipped' ) message.warning( 'Nothing was exported. Open Runs to see why.' );
			else if ( run?.status === 'failed' ) message.error( run?.error || 'The export failed. Open Runs for the detail.' );
			else if ( run?.truncated ) {
				message.warning( `Exported the first ${ Number( run.rows ).toLocaleString() } of ${ Number( run.total ).toLocaleString() } items.` );
			}
			else message.success( 'Export run.' );
			await reload();
			update( { runsExport: id, runsPage: 1 } );
		}
		catch ( err ) {
			const status = err?.response?.status;
			if ( status === 429 ) {
				// 429 export_run_limit: past maxManualRunsPerHour manual runs of
				// one definition in a rolling hour (scheduled runs do not count).
				const wait = retryAfterMs( err );
				const minutes = wait !== null && wait > 0 ? Math.ceil( wait / 60000 ) : null;
				message.error( `An export can be run by hand ${ bounds.maxManualRunsPerHour } times an hour.${ minutes
					? ` Try again in ${ formatCount( minutes, 'minute' ) }.`
					: ' Try again later.' }` );
			}
			else {
				message.error( err?.response?.data?.message || 'Unable to run this export.' );
			}
		}
		finally {
			setRunning( null );
		}
	}, [ collectionId, message, reload, update, bounds.maxManualRunsPerHour ] );

	// It mails the recipients, exactly as a scheduled run would — asked first.
	const confirmRunNow = useCallback( definition => {
		const recipients = definition?.delivery?.recipients || [];
		const emailOn = definition?.delivery?.channels?.email?.enabled !== false;
		modal.confirm( {
			title: `Run "${ definition?.name || 'this export' }" now?`,
			content: recipients.length && emailOn
				? `It is made now and emailed to ${ formatCount( recipients.length, 'recipient' ) }, just as a scheduled run would be.`
				: 'It is made now and kept for you to download from Runs. Nobody is emailed.',
			okText: 'Run now',
			onOk: () => runNow( definition ),
		} );
	}, [ modal, runNow ] );

	const confirmDelete = useCallback( definition => {
		const id = normalizeId( definition?._id );
		confirmDestructive( modal, {
			title: `Delete "${ definition?.name || 'this export' }"?`,
			content: 'It stops running at once, and every download link it has sent stops working. History can restore the export, but not those links.',
			okText: 'Delete',
			onOk: async () => {
				try {
					await deleteExportDefinition( collectionId, id );
					message.success( 'Export deleted.' );
					await reload();
				}
				catch ( err ) {
					message.error( err?.response?.data?.message || 'Unable to delete this export.' );
				}
			},
		} );
	}, [ collectionId, message, modal, reload ] );

	const atLimit = definitions.length >= bounds.maxDefinitions;

	const menuFor = definition => {
		const ownerId = normalizeId( definition?.ownerId );
		const isOwnerRow = Boolean( me ) && ownerId === me;
		const id = normalizeId( definition?._id );
		return buildActionMenu( [
			{
				key: 'runs',
				group: 'record',
				icon: <HISTORY />,
				label: 'Runs…',
				onClick: () => update( { runsExport: id, runsPage: 1 } ),
			},
			{
				key: 'edit',
				group: 'record',
				glyph: 'edit',
				label: 'Edit…',
				onClick: () => setEditing( { definition } ),
			},
			{
				key: 'duplicate',
				group: 'record',
				glyph: 'duplicate',
				label: 'Duplicate…',
				disabled: atLimit,
				title: atLimit ? `A collection holds at most ${ bounds.maxDefinitions } exports.` : undefined,
				onClick: () => setEditing( { prefill: duplicateOf( definition ) } ),
			},
			{
				key: 'run',
				group: 'share',
				icon: <SEND />,
				label: isOwnerRow ? 'Run now…' : 'Run now (owner only)',
				disabled: ! isOwnerRow || running === id,
				title: isOwnerRow ? undefined : 'Only the owner can run this export — edit and save to take ownership.',
				onClick: () => confirmRunNow( definition ),
			},
			{
				key: 'delete',
				group: 'destructive',
				glyph: 'delete',
				label: 'Delete…',
				onClick: () => confirmDelete( definition ),
			},
		] );
	};

	const openDefinition = useMemo(
		() => definitions.find( row => normalizeId( row?._id ) === runsExport ) || null,
		[ definitions, runsExport ]
	);

	const retryLoad = () => {
		setLoading( true );
		reload().finally( () => setLoading( false ) );
	};
	if ( ! loaded ) {
		return (
			<TabLoadState
				loading={loading}
				error={loadError}
				label='exports'
				errorDetail='Exports are for managers of this collection. If your access just changed, that may be why.'
				onRetry={retryLoad}
			/>
		);
	}

	const describeContext = { cardsById, savedFilters };

	return (
		<div className='settings-column exports-tab'>
			{loadError ? <RefreshFailedNotice label='exports' onRetry={retryLoad} /> : null}

			<Alert
				type='info'
				showIcon
				message='Changes on this tab apply immediately'
				description='A saved export runs on its schedule, or when its owner runs it, and emails each recipient a download link. It runs with its owner’s access.'
			/>

			<section className='form-panel'>
				<div className='form-panel__kicker-row'>
					<div className='micro-label form-panel__kicker'>Exports</div>
					<Button
						icon={<PLUS />}
						disabled={atLimit}
						onClick={() => setEditing( { prefill: null } )}
					>
						New export
					</Button>
				</div>
				{atLimit ? (
					<Text type='secondary' className='form-panel__hint'>
						{`A collection holds at most ${ bounds.maxDefinitions } exports. Delete one to make another.`}
					</Text>
				) : null}

				<List
					className='exports-list'
					dataSource={definitions}
					rowKey={row => normalizeId( row?._id )}
					locale={{ emptyText: (
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description='No exports yet. Create one to send a list on a schedule.'
						/>
					) }}
					renderItem={definition => {
						const id = normalizeId( definition?._id );
						const name = definition?.name || 'Untitled export';
						const ownerId = normalizeId( definition?.ownerId );
						const menu = menuFor( definition );
						return (
							<List.Item key={id} className='exports-list__item'>
								<div className='exports-list__row'>
									<div className='exports-list__main'>
										<div className='exports-list__name'>
											<Text strong ellipsis={{ tooltip: name }}>{name}</Text>
										</div>
										<Text type='secondary' className='exports-list__line'>
											{describeDefinition( definition, describeContext )}
										</Text>
										<Text type='secondary' className='exports-list__line'>
											{describeRecipients( definition )}
										</Text>
										<div className='exports-list__line exports-list__meta'>
											<LastRun definition={definition} />
											{definition?.missedRuns > 0 ? (
												<Text type='secondary'>{`Missed ${ formatCount( definition.missedRuns, 'run' ) }`}</Text>
											) : null}
											{ownerId ? (
												<span className='exports-list__owner'>
													<Text type='secondary'>{ownerId === me ? 'Runs with your access' : 'Runs as'}</Text>
													{ownerId === me ? null : <UserRef userId={ownerId} size={18} />}
												</span>
											) : null}
										</div>
										{definition?.disabledReason ? (
											<Text className='exports-list__line exports-list__reason'>
												{DISABLED_REASON_WORDS[ definition.disabledReason ] || 'Switched off by keepr.'}
												{' Switching it on makes you the owner.'}
											</Text>
										) : null}
									</div>
									<Switch
										checked={definition?.enabled === true}
										onChange={next => handleToggle( definition, next )}
										loading={running === id}
										aria-label={`${ name } ${ definition?.enabled ? 'is on' : 'is off' }`}
									/>
									{menu ? (
										<Dropdown trigger={[ 'click' ]} placement='bottomRight' menu={menu}>
											<Button icon={<KEBAB />} aria-label={`Actions for ${ name }`} />
										</Dropdown>
									) : null}
								</div>
							</List.Item>
						);
					}}
				/>
			</section>

			{editing ? (
				<ExportDefinitionEditor
					open
					collectionId={collectionId}
					definition={editing.definition || null}
					prefill={editing.prefill || null}
					savedFilters={savedFilters}
					cards={collectionCards}
					getElementsForCard={getElementsForCard}
					recipientOptions={recipientOptions}
					recipientsLoading={sharingLoading}
					limits={limits}
					channels={channels}
					onClose={closeEditor}
					onSaved={() => { closeEditor(); reload(); }}
				/>
			) : null}

			{runsExport ? (
				<ExportRunsModal
					open
					collectionId={collectionId}
					definition={openDefinition || { _id: runsExport }}
					page={runsPage}
					onPage={page => update( { runsPage: page } )}
					onClose={() => update( { runsExport: undefined, runsPage: 1 } )}
				/>
			) : null}

		</div>
	);
}
